import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatError, fetchChatTaskStatus, startChatTask, type ChatResponse } from '../api/grampsChat';
import { readBearerToken } from '../grampsAuth';

const POLL_INTERVAL_MS = 1500;
const TERMINAL_STATES = new Set(['SUCCESS', 'FAILURE', 'REVOKED']);

export type ChatTurn = { role: 'user' | 'assistant' | 'error'; content: string };

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Keeps the raw message history (not the display turns) across a conversation:
 * message_history_raw preserves full tool-call context server-side, so passing
 * it back is what lets a follow-up question like "and her sister?" resolve
 * without re-sending (and re-tokenizing) the whole chat transcript ourselves.
 */
export function useChatConversation(treeName: string | null) {
    const [turns, setTurns] = useState<ChatTurn[]>([]);
    const [pending, setPending] = useState(false);
    const messageHistoryRaw = useRef<string | null>(null);
    const cancelledRef = useRef(false);

    // React StrictMode double-invokes effects in dev (mount, cleanup, remount) to
    // surface exactly this kind of bug: without the reset here, that simulated
    // cleanup would permanently flip cancelledRef to true before the user ever
    // sends a message, silently dropping every response for the rest of the
    // component's life -- the poll loop and its post-await guards both bail out
    // on a ref that never comes back down.
    useEffect(() => {
        cancelledRef.current = false;
        return () => {
            cancelledRef.current = true;
        };
    }, []);

    const send = useCallback(async (query: string) => {
        const token = readBearerToken(treeName);
        if (!token) {
            setTurns((prev) => [...prev, { role: 'user', content: query }, { role: 'error', content: 'Vous devez être connecté pour utiliser l\'assistant.' }]);
            return;
        }

        setTurns((prev) => [...prev, { role: 'user', content: query }]);
        setPending(true);
        try {
            const task = await startChatTask(query, token, { messageHistoryRaw: messageHistoryRaw.current });
            let status = await fetchChatTaskStatus(task.id, token);
            while (!cancelledRef.current && !TERMINAL_STATES.has(status.state ?? '')) {
                await sleep(POLL_INTERVAL_MS);
                status = await fetchChatTaskStatus(task.id, token);
            }
            if (cancelledRef.current) return;

            if (status.state === 'SUCCESS') {
                const result = status.result_object as ChatResponse | undefined;
                messageHistoryRaw.current = result?.message_history_raw ?? messageHistoryRaw.current;
                setTurns((prev) => [...prev, { role: 'assistant', content: result?.response || '(réponse vide)' }]);
            } else {
                const message = typeof status.info === 'string' && status.info
                    ? status.info
                    : 'Le fournisseur de modèle IA a renvoyé une erreur.';
                setTurns((prev) => [...prev, { role: 'error', content: message }]);
            }
        } catch (error) {
            if (cancelledRef.current) return;
            const message = error instanceof ChatError || error instanceof Error ? error.message : 'Erreur inconnue.';
            setTurns((prev) => [...prev, { role: 'error', content: message }]);
        } finally {
            if (!cancelledRef.current) setPending(false);
        }
    }, [treeName]);

    const reset = useCallback(() => {
        setTurns([]);
        messageHistoryRaw.current = null;
    }, []);

    return { turns, pending, send, reset };
}
